import { useEffect, useRef, useState } from "react";
import { FileText, Info, Lock } from "lucide-react";

import { cn } from "@/lib/utils";
import { SCORE_EFFORT } from "@/lib/domain-constants";
import { formatDate, formatMoney } from "@/lib/format";
import {
  AnalysisHero,
  EmptyCompanyHero,
  OUTCOMES,
  ReadyToAnalyzeHero,
  countByOutcome,
} from "./BidRequirementsHero";
import { useMyCompanyDocuments } from "@/hooks/queries/useMyCompanyDocuments";
import { useRunBidAnalysis } from "@/hooks/mutations/useRunBidAnalysis";
import type {
  BidAnalysis,
  BidAnalysisRow,
  BidRequirement,
  BidRequirementCategory,
} from "@/types/domain";

/**
 * Lo que las bases del proceso le exigen a quien quiera postular, leído con IA,
 * frente a lo que la empresa puede acreditar.
 *
 * Tiene dos estados y la tabla es la misma en los dos, solo que con el cruce se
 * llenan las columnas "Tu empresa" y "Estado". Sin cruce se muestra el texto
 * **verbatim** de las bases —que es la prueba, y por eso la cita de página—; con
 * cruce se muestra el `piden`, el mismo requisito ya resumido para caber en una
 * fila.
 */

type BidRequirementsData = {
  isFinal: boolean;
  /** El OECE ya publicó las integradas, las tengamos leídas o no. */
  finalIsPublished: boolean;
  fileName: string;
  publishedAt: string | Date | null;
  url: string | null;
  totalPages: number | null;
  totalScore: number | null;
  requirements: BidRequirement[];
};

/** Una fila de la tabla, venga de la extracción sola o del cruce. */
type Row = {
  nombre: string;
  categoria: BidRequirementCategory;
  texto: string;
  /** El umbral comparable. Solo sin cruce: el `piden` ya lo lleva dentro. */
  valor: string | null;
  pagina: number | null;
  cruce: BidAnalysisRow | null;
};

// El orden es el de la decisión, no el del backend: primero lo que descalifica,
// después lo que suma puntos, y al final las condiciones del contrato — que no
// se cruzan contra nada, solo se leen. Los INFORMATIVO no llegan hasta acá (ver
// `useContractingDetailModel`).
const CATEGORIES: {
  key: BidRequirementCategory;
  title: string;
  /** Su nombre en el filtro, donde no cabe el título largo. */
  chip: string;
  hint: string;
  pill: string;
  /**
   * Si se compara contra los documentos de la empresa. Las condiciones del
   * contrato no: aplican igual a todo el mundo, así que su tabla se queda en
   * las dos columnas que sí dicen algo.
   */
  evaluable: boolean;
}[] = [
  {
    key: "DURO",
    title: "Requisitos de calificación",
    chip: "Requisitos de calificación",
    hint: "No cumplirlos descalifica.",
    pill: "bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300",
    evaluable: true,
  },
  {
    key: "PUNTAJE",
    title: "Factores de evaluación",
    chip: "Factores de puntaje",
    hint: "No son obligatorios, pero suman puntos en la evaluación.",
    pill: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300",
    evaluable: true,
  },
  {
    key: "CONTRACTUAL",
    title: "Condiciones del contrato",
    chip: "Condiciones",
    hint: "Plazos, garantías, adelantos, penalidades y seguros.",
    pill: "bg-sky-50 text-sky-700 dark:bg-sky-950/30 dark:text-sky-300",
    evaluable: false,
  },
];

const STATUS_LABELS: Record<BidAnalysisRow["estado"], string> = {
  CUMPLE: "Cumple",
  CUMPLE_CON_OBSERVACION: "Cumple con obs.",
  SE_COMPROMETE: "Se compromete",
  REVISAR: "Revisar",
  NO_CUMPLE: "No cumple",
  NO_APLICA: "No aplica",
};

const STATUS_PILLS: Record<BidAnalysisRow["estado"], string> = {
  CUMPLE:
    "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300",
  CUMPLE_CON_OBSERVACION:
    "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300",
  SE_COMPROMETE:
    "bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300",
  REVISAR:
    "bg-orange-50 text-orange-700 dark:bg-orange-950/30 dark:text-orange-300",
  NO_CUMPLE: "bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300",
  NO_APLICA: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
};

/**
 * El `valor` de un requisito es texto libre: un monto, unos años, una cantidad,
 * unos puntos. Se le pone unidad solo cuando viene como número pelado, porque
 * un "5" a secas no dice nada:
 *
 * - En un factor de evaluación siempre son puntos.
 * - En el resto, cuatro cifras o más es plata: en obra pública ningún umbral de
 *   cantidad, años o meses llega a mil.
 * - Y por debajo de mil es una cantidad — un residente, dos volquetes.
 */
function formatRequirementValue(
  valor: string,
  categoria: BidRequirementCategory,
): string {
  const numeric = Number(valor);
  if (!Number.isFinite(numeric)) return valor;
  if (categoria === "PUNTAJE") return `${numeric} pts.`;
  return numeric >= 1000 ? formatMoney(numeric) : `${numeric} und.`;
}

/** A partir de acá el texto no entra en tres líneas y se ofrece desplegarlo. */
const CLAMP_THRESHOLD_CHARS = 200;

/**
 * Las filas que pinta la tabla. Con cruce mandan sus filas —traen el requisito
 * ya resumido y lo que acredita la empresa—; sin él, la extracción sola.
 */
function buildRows(
  requirements: BidRequirement[],
  analysis: BidAnalysis | null,
): Row[] {
  if (!analysis) {
    return requirements.map((requirement) => ({
      nombre: requirement.nombre,
      categoria: requirement.categoria,
      texto: requirement.texto,
      valor: requirement.valor,
      pagina: requirement.pagina,
      cruce: null,
    }));
  }

  return analysis.rows
    .filter((row) => row.categoria !== "INFORMATIVO")
    .map((row) => ({
      nombre: row.requisito,
      categoria: row.categoria,
      texto: row.piden,
      valor: null,
      pagina: row.pagina,
      cruce: row,
    }));
}

/** Una fila de chips de filtro. Las dos filas —categoría y resultado— son iguales. */
function FilterChips({
  options,
  active,
  onSelect,
}: {
  options: { key: string; label: string; count: number }[];
  active: string;
  onSelect: (key: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {options.map((option) => (
        <button
          key={option.key}
          type="button"
          onClick={() => onSelect(option.key)}
          className={cn(
            "cursor-pointer rounded-full border px-3 py-1 text-[13px] font-medium transition-colors",
            active === option.key
              ? "border-primary bg-primary text-primary-foreground"
              : "text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800/50",
          )}
        >
          {option.label} · {option.count}
        </button>
      ))}
    </div>
  );
}

function RequirementRow({
  row,
  evaluable,
  onOpenPage,
}: {
  row: Row;
  evaluable: boolean;
  onOpenPage?: (page: number) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const { nombre, texto, valor, pagina, cruce, categoria } = row;

  return (
    <tr className="border-b align-top last:border-b-0">
      <td className="p-4">
        <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
          {nombre}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {valor && (
            <span className="whitespace-nowrap rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-primary">
              {formatRequirementValue(valor, categoria)}
            </span>
          )}
          {cruce?.puntos && (
            <span
              className={cn(
                "whitespace-nowrap text-[11px] font-semibold",
                SCORE_EFFORT[cruce.puntos.esfuerzo]?.tone ?? "text-slate-500",
              )}
            >
              {cruce.puntos.acreditados}/{cruce.puntos.delFactor} pts ·{" "}
              {SCORE_EFFORT[cruce.puntos.esfuerzo]?.label ?? cruce.puntos.esfuerzo}
            </span>
          )}
          {pagina !== null &&
            (onOpenPage ? (
              <button
                type="button"
                onClick={() => onOpenPage(pagina)}
                className="cursor-pointer text-[11px] text-slate-400 underline-offset-2 hover:text-primary hover:underline"
              >
                pág. {pagina}
              </button>
            ) : (
              <span className="text-[11px] text-slate-400">pág. {pagina}</span>
            ))}
        </div>
      </td>

      <td className="p-4">
        <p
          className={cn(
            "text-[13px] leading-relaxed text-slate-600 dark:text-slate-300",
            !expanded && "line-clamp-3",
          )}
        >
          {texto}
        </p>
        {texto.length > CLAMP_THRESHOLD_CHARS && (
          <button
            type="button"
            onClick={() => setExpanded((open) => !open)}
            className="mt-1 cursor-pointer text-[11px] font-semibold text-primary hover:underline"
          >
            {expanded ? "Ver menos" : "Ver más"}
          </button>
        )}
      </td>

      {evaluable && (
        <>
          <td className="p-4">
            {cruce ? (
              <>
                <p className="text-[13px] leading-relaxed text-slate-700 dark:text-slate-200">
                  {cruce.tiene}
                </p>
                {cruce.porque && (
                  <p className="mt-1 text-[12px] italic leading-relaxed text-slate-400">
                    {cruce.porque}
                  </p>
                )}
              </>
            ) : (
              /* Sin documentos no hay nada que comparar. El candado dice que el
                 hueco es nuestro, no del pliego. */
              <div className="flex items-center gap-2 rounded-lg border border-dashed bg-slate-50/60 px-3 py-2 text-[13px] text-slate-400 dark:bg-slate-900/20">
                <Lock className="size-3.5 shrink-0" />
                Sin documentos cargados
              </div>
            )}
          </td>

          <td className="p-4 text-right">
            <span
              className={cn(
                "whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                cruce
                  ? STATUS_PILLS[cruce.estado]
                  : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
              )}
            >
              {cruce ? STATUS_LABELS[cruce.estado] : "Sin evaluar"}
            </span>
          </td>
        </>
      )}
    </tr>
  );
}

export function BidRequirementsTab({
  contractingId,
  data,
  analysis,
  onOpenDocument,
}: {
  contractingId: number;
  data: BidRequirementsData;
  /** El cruce contra los documentos de la empresa, si ya se corrió. */
  analysis: BidAnalysis | null;
  /** Abre el PDF de las bases, en la página citada si se pidió una. */
  onOpenDocument?: (page?: number) => void;
}) {
  const [category, setCategory] = useState("todos");
  const [outcome, setOutcome] = useState("todos");

  // Solo se consultan los documentos cuando no hay cruce: con cruce, la
  // cabecera muestra el veredicto y el checklist no pinta nada.
  const { data: documents } = useMyCompanyDocuments(!analysis);
  const runAnalysis = useRunBidAnalysis();

  // La barra de filtros se queda fija arriba, y los títulos de sección justo
  // debajo. Su alto se mide porque cambia: con cruce son dos filas de chips.
  const barRef = useRef<HTMLDivElement>(null);
  const [barHeight, setBarHeight] = useState(0);
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const observer = new ResizeObserver(() => setBarHeight(bar.offsetHeight));
    observer.observe(bar);
    setBarHeight(bar.offsetHeight);
    return () => observer.disconnect();
  }, []);

  const rows = buildRows(data.requirements, analysis);
  const hardCount = rows.filter((r) => r.categoria === "DURO").length;
  const scoreRows = data.requirements.filter((r) => r.categoria === "PUNTAJE");

  // Las secciones no cambian haya cruce o no: reagrupar la tabla según el
  // estado de la empresa obligaría a reaprender la pantalla. El resultado se
  // filtra en una segunda fila, encima de la de siempre.
  const categoryFilters = [
    { key: "todos", label: "Todos", count: rows.length, test: () => true },
    ...CATEGORIES.map((item) => ({
      key: item.key as string,
      label: item.chip,
      count: rows.filter((r) => r.categoria === item.key).length,
      test: (row: Row) => row.categoria === item.key,
    })),
  ].filter((f) => f.count > 0);

  const categoryTest =
    categoryFilters.find((f) => f.key === category)?.test ?? (() => true);

  // Los chips de resultado cuentan DENTRO de la categoría elegida: si estás
  // mirando los factores de puntaje, "Por cerrar · 3" tiene que ser 3 factores,
  // no 3 requisitos de cualquier tipo.
  const inCategory = rows.filter(categoryTest);
  const crossedInCategory = inCategory.flatMap((r) => (r.cruce ? [r.cruce] : []));

  const outcomeFilters = analysis
    ? [
        {
          key: "todos",
          label: "Todos",
          count: crossedInCategory.length,
          test: () => true,
        },
        ...OUTCOMES.map((item) => ({
          key: item.key as string,
          label: item.chip,
          count: countByOutcome(crossedInCategory, item.key),
          test: (row: Row) =>
            !!row.cruce && item.statuses.includes(row.cruce.estado),
        })),
      ].filter((f) => f.count > 0)
    : [];

  const outcomeTest =
    outcomeFilters.find((f) => f.key === outcome)?.test ?? (() => true);
  const visible = inCategory.filter(outcomeTest);

  const groups = CATEGORIES.map((item) => ({
    ...item,
    rows: visible.filter((r) => r.categoria === item.key),
  })).filter((group) => group.rows.length > 0);

  const scoreGap = findScoreGap(analysis, scoreRows, data.totalScore);

  const openPage =
    data.url && onOpenDocument
      ? (page: number) => onOpenDocument(page)
      : undefined;

  return (
    <>
      {analysis ? (
        <AnalysisHero
          analysis={analysis}
          onRerun={() => runAnalysis.mutate({ contracting_id: contractingId })}
          isRunning={runAnalysis.isPending}
        />
      ) : documents && documents.readyDocuments > 0 ? (
        <ReadyToAnalyzeHero
          totalScore={data.totalScore}
          requirementCount={rows.length}
          documents={documents}
          onRun={() => runAnalysis.mutate({ contracting_id: contractingId })}
          isRunning={runAnalysis.isPending}
        />
      ) : (
        <EmptyCompanyHero
          totalScore={data.totalScore}
          hardCount={hardCount}
          documents={documents ?? null}
        />
      )}

      {/* Filtros, y de qué documento sale todo esto. Pegada arriba: con 36
          requisitos se scrollea largo y perder los filtros obliga a subir. */}
      <div
        ref={barRef}
        className="sticky top-0 z-20 -mx-6 flex flex-col gap-3 border-b bg-card px-6 pb-4 pt-6"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <FilterChips
            options={categoryFilters}
            active={category}
            onSelect={setCategory}
          />

          <div className="flex min-w-0 items-center gap-2 text-[13px] text-slate-400">
            <span className="shrink-0">Fuente:</span>
            <div
              className="flex min-w-0 items-center gap-2 rounded-lg border bg-card px-3 py-1.5"
              title={data.fileName}
            >
              <FileText className="size-3.5 shrink-0 text-slate-400" />
              <span className="truncate font-semibold text-slate-600 dark:text-slate-300">
                {data.isFinal ? "Bases integradas" : "Bases administrativas"}
                {data.totalPages ? ` · ${data.totalPages} págs.` : ""}
                {data.publishedAt
                  ? ` · ${formatDate(String(data.publishedAt))}`
                  : ""}
              </span>
              {data.url && onOpenDocument && (
                <button
                  type="button"
                  onClick={() => onOpenDocument()}
                  className="shrink-0 cursor-pointer font-semibold text-primary hover:underline"
                >
                  Ver
                </button>
              )}
            </div>
          </div>
        </div>

        {outcomeFilters.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
              Tu empresa
            </span>
            <FilterChips
              options={outcomeFilters}
              active={outcome}
              onSelect={setOutcome}
            />
          </div>
        )}
      </div>

      {/* Las integradas ya existen: lo que se está leyendo quedó viejo. */}
      {!data.isFinal && data.finalIsPublished && (
        <p className="mb-4 flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-xs leading-relaxed text-amber-800 dark:bg-amber-950/20 dark:text-amber-300">
          <Info className="mt-0.5 size-3.5 shrink-0" />
          La entidad ya publicó las bases integradas, que son las definitivas.
          Esto es la versión administrativa: contrástala con ellas antes de
          decidir.
        </p>
      )}

      {/* El cuadro de factores declara un total; si los extraídos no lo suman,
          falta alguno y hay que decirlo en vez de dar la lista por buena. */}
      {scoreGap && (
        <p className="mb-4 rounded-xl bg-amber-50 p-3 text-xs leading-relaxed text-amber-800 dark:bg-amber-950/20 dark:text-amber-300">
          Los factores extraídos suman {scoreGap.extracted} de los{" "}
          {scoreGap.declared} puntos que declara el pliego: puede faltar alguno.
          Conviene revisarlo en el documento.
        </p>
      )}

      {groups.length === 0 ? (
        <div className="rounded-2xl border border-dashed bg-slate-50 p-12 text-center text-sm italic text-slate-400 dark:bg-slate-800/20">
          No hay requisitos en esta vista.
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          {groups.map((group) => (
            <div key={group.key}>
              <div
                className="sticky z-10 -mx-6 flex flex-wrap items-center gap-2.5 bg-card px-6 pb-3 pt-2"
                style={{ top: barHeight }}
              >
                <span
                  className={cn(
                    "rounded-md px-2 py-0.5 text-xs font-bold tabular-nums",
                    group.pill,
                  )}
                >
                  {group.rows.length}
                </span>
                <span className="text-base font-bold text-slate-800 dark:text-slate-100">
                  {group.title}
                </span>
                <span className="text-[13px] text-slate-400">{group.hint}</span>
              </div>

              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="border-b text-[10px] uppercase tracking-wider text-slate-400">
                    <th className="w-[22%] px-4 pb-2 font-bold">Requisito</th>
                    <th className="px-4 pb-2 font-bold">
                      Lo que exigen las bases
                    </th>
                    {group.evaluable && (
                      <>
                        <th className="w-[28%] px-4 pb-2 font-bold">
                          Tu empresa
                        </th>
                        <th className="px-4 pb-2 text-right font-bold">
                          Estado
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map((row, i) => (
                    <RequirementRow
                      key={`${group.key}-${i}`}
                      row={row}
                      evaluable={group.evaluable}
                      onOpenPage={openPage}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * Cuánto se perdió al leer los factores, si es que se perdió algo. Con cruce lo
 * resuelve el backend al sumarlos (`tallyPoints`); sin cruce hay que sumar los
 * `valor`, que son texto libre — y si ninguno es un número no se puede afirmar
 * nada, así que se calla en vez de decir "0 de 100".
 */
function findScoreGap(
  analysis: BidAnalysis | null,
  scoreRows: BidRequirement[],
  declaredTotal: number | null,
): { extracted: number; declared: number } | null {
  if (analysis?.points) {
    const declared = analysis.points.declaredTotal ?? declaredTotal;
    return analysis.points.incompleto && declared !== null
      ? { extracted: analysis.points.disponible, declared }
      : null;
  }

  if (declaredTotal === null) return null;
  const parsed = scoreRows
    .map((row) => Number(row.valor))
    .filter((value) => Number.isFinite(value));
  if (parsed.length === 0) return null;

  const extracted = parsed.reduce((sum, value) => sum + value, 0);
  return extracted < declaredTotal
    ? { extracted, declared: declaredTotal }
    : null;
}
